'use client';

import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Author {
  name: string;
  image: string;
  alt: string;
}

interface Article {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: Author;
  publishDate: string;
  readTime: string;
  image: string;
  alt: string;
  tags: string[];
}

interface FeaturedArticlesProps {
  articles: Article[];
}

const FeaturedArticles = ({ articles }: FeaturedArticlesProps) => {
  return (
    <section className="py-12 md:py-16 lg:py-24 px-4 md:px-8 lg:px-20 bg-zinc-50">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-10 md:mb-12">
          <div>
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-black/5 backdrop-blur-xl border border-black/10 mb-4">
              <div className="w-1.5 h-1.5 bg-black rounded-full" />
              <span className="text-xs font-medium text-black/90 tracking-widest uppercase">
                Featured
              </span>
            </div>
            <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl font-normal text-black tracking-tight leading-tight">
              Featured Articles
            </h2>
            <p className="text-black/70 font-normal mt-4">
              Our most popular and insightful pieces
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {articles.map((article) => (
            <Link
              key={article.id}
              href={`/blog/${article.slug}`}
              className="group bg-white rounded-2xl overflow-hidden border border-zinc-200 hover:border-zinc-300 hover:shadow-lg transition-all duration-300"
            >
              <div className="relative h-64 overflow-hidden">
                <AppImage
                  src={article.image}
                  alt={article.alt}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-[#1B5E4F] text-white text-xs font-medium rounded-full">
                    Featured
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <span className="px-3 py-1 bg-[#1B5E4F]/10 text-[#1B5E4F] text-xs font-medium rounded-full">
                    {article.category}
                  </span>
                  <span className="text-black/50 text-xs">
                    {article.readTime}
                  </span>
                </div>

                <h3 className="font-headline text-xl font-normal text-black mb-3 group-hover:text-[#1B5E4F] transition-colors duration-200 line-clamp-2">
                  {article.title}
                </h3>

                <p className="text-sm text-black/70 font-normal mb-4 line-clamp-3">
                  {article.excerpt}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-zinc-200">
                  <div className="flex items-center space-x-3">
                    <div className="relative w-10 h-10 rounded-full overflow-hidden">
                      <AppImage
                        src={article.author.image}
                        alt={article.author.alt}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-black">
                        {article.author.name}
                      </p>
                      <p className="text-xs text-black/50">
                        {new Date(article.publishDate).toLocaleDateString('en-IN', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>

                  <Icon
                    name="ArrowRightIcon"
                    size={20}
                    className="text-[#1B5E4F] group-hover:translate-x-1 transition-transform duration-200"
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedArticles;